// Frontend mirror of backend/sources/models.py. Field names match
// byte-for-byte so payloads round-trip through JSON without re-mapping.
// Keep in sync if backend/sources/models.py changes.

export type SourceType =
  | "git"
  | "openapi"
  | "redocly"
  | "url"
  | "s3"
  | "gcs"
  | "azure"
  | "notion"
  | "confluence"
  | "drive"
  | "readme"
  | "custom";

export type SyncPolicy = "webhook" | "5m" | "1h" | "6h" | "daily" | "manual";

export type ReviewPolicy =
  | "review_required"
  | "auto_publish_high_confidence"
  | "auto_publish_all";

export type SyncStatus =
  | "ok"
  | "auth_failed"
  | "rate_limited"
  | "partial"
  | "skipped_unchanged"
  | "never";

export interface SourceRecord {
  id: string;
  org_slug: string;
  type: SourceType;
  config: Record<string, unknown>;
  auth_ref: string;
  sync_policy: SyncPolicy;
  review_policy: ReviewPolicy;
  created_at: string;
  last_sync_at: string | null;
  last_sync_status: SyncStatus;
}

export interface SourceSyncRun {
  id: string;
  source_id: string;
  started_at: string;
  finished_at: string | null;
  items_listed: number;
  items_changed: number;
  items_deleted: number;
  error: string | null;
}

// Body shape for POST /sources. `config` is adapter-specific; `auth_value`
// is the raw secret the backend swaps for an `auth_ref`.
export interface CreateSourceBody {
  type: SourceType;
  config: Record<string, unknown>;
  auth_value: string;
  sync_policy: SyncPolicy;
  review_policy: ReviewPolicy;
}

// Adapter availability per PRD §11 roadmap. v1 ships openapi + git +
// redocly. The redocly tile (PRD 13) is a navigator over openapi — one
// redocly.yaml → N specs → N doc_ids.
export interface AdapterMeta {
  type: SourceType;
  label: string;
  available: boolean;
  tier: string;
}

export const ADAPTERS: AdapterMeta[] = [
  { type: "openapi", label: "OpenAPI URL", available: true, tier: "v1" },
  { type: "git", label: "Git repository", available: true, tier: "v1" },
  { type: "redocly", label: "Redocly project", available: true, tier: "v1" },
  { type: "url", label: "Live site (sitemap)", available: false, tier: "v1.2" },
  { type: "s3", label: "S3 prefix", available: false, tier: "v1.3" },
  { type: "notion", label: "Notion workspace", available: false, tier: "v1.3" },
  { type: "confluence", label: "Confluence space", available: false, tier: "v1.4" },
  { type: "drive", label: "Google Drive", available: false, tier: "v1.4" },
  { type: "gcs", label: "Google Cloud Storage", available: false, tier: "v1.5" },
  { type: "azure", label: "Azure Blob", available: false, tier: "v1.5" },
];
